// Mapeia a resposta real do backend (CardSupervisaoAtivoDto[], endpoint
// GET /supervisao/{ativoId}/cards) para o mesmo formato "readings" que
// cardsConfig.js e os componentes CmaReadingCard/CmaDashboard já consomem
// (criados originalmente em cima de cmaDashboardMockData.js).
//
// O backend já manda cada campo com `nome` (texto traduzido) e
// `unidadeMedida` prontos para exibição — a maioria dos cards usa esses
// campos DIRETO, sem mapear código a código: um campo novo que o backend
// adicionar a um módulo já suportado aparece na tela sem precisar de mudança
// nenhuma aqui (ver buildDynamicRows). Buchas é o único com layout próprio
// (1 linha por fase, 3 medidas juntas) para os 9 campos já conhecidos (ver
// CODES.bushings) — pedido explícito de produto, sem equivalente genérico —,
// mas um campo NOVO nesse módulo (fora desses 9) não some: vira linha extra
// no padrão genérico, igual aos outros cards (ver buildBushings).
//
// O backend hoje só cobre os VALORES dos 7 cards. Vários pedaços que o mock
// tinha não existem em lugar nenhum do backend ainda — ficam com um
// fallback explícito aqui, não inventados:
//   - cardStatuses (badge normal/atenção/crítico por card): sem threshold
//     de valor no backend — todo card com dado começa 'normal' e só sai daí
//     por alarme ativo do módulo correspondente (ver
//     applyAlarmStatuses/CARD_KEY_BY_MODULE_CODE abaixo, que usa a mesma
//     fonte de alarmes de `alarms`, não mais um placeholder).
//   - summary.generalStatus/nextMaintenanceDate/operatingDays: não existem
//     no backend — ficam com placeholder. summary.assetCondition já tem
//     fonte real (ver computeAssetCondition), não é mais placeholder.
//   - paperInfo (Água-Papel): fora de escopo por decisão explícita do spec
//     (decisão 11) — painel fica oculto (ver CmaDashboard.vue).
//
// Não há mais painel de alarmes no CMA (removido — ver CmaDashboard.vue) —
// `alarms` só entra como insumo de `applyAlarmStatuses`/`computeAssetCondition`
// abaixo, nunca é exposto em `readings`.

const CODES = {
  bushings: {
    capacitance: {
      FA: 'GET_IndicacaoDeCapacitanciaConjunto1FaseA',
      FB: 'GET_IndicacaoDeCapacitanciaConjunto1FaseB',
      FC: 'GET_IndicacaoDeCapacitanciaConjunto1FaseC',
    },
    tangentDelta: {
      FA: 'GET_IndicacaoDeTangenteDeltaConjunto1FaseA',
      FB: 'GET_IndicacaoDeTangenteDeltaConjunto1FaseB',
      FC: 'GET_IndicacaoDeTangenteDeltaConjunto1FaseC',
    },
    leakageCurrent: {
      FA: 'GET_IndicacaoDeCorrenteDeFugaConjunto1FaseA',
      FB: 'GET_IndicacaoDeCorrenteDeFugaConjunto1FaseB',
      FC: 'GET_IndicacaoDeCorrenteDeFugaConjunto1FaseC',
    },
  },
  // Exceções pontuais que ainda precisam achar UM campo específico dentro de
  // um card dinâmico (ver buildBagCard/buildElectricalDataCard) — não
  // mapeiam mais um card inteiro campo a campo como CODES.bushings acima.
  bagStatus: 'GET_IndicacaoDeAlarmePorRupturaDaMembrana',
  electricalPower: 'PotenciaNominal',
};

const PHASES = ['FA', 'FB', 'FC'];

// Indexa todos os campos de todos os cards retornados num único dicionário
// por código — usado só por Buchas (ver buildBushings), que continua
// identificando campos por `codigo` em vez do `nome` dinâmico.
function indexCamposPorCodigo(cards) {
  const byCode = {};
  (cards || []).forEach((card) => {
    (card.campos || []).forEach((campo) => {
      if (campo.codigo) byCode[campo.codigo] = campo;
    });
  });
  return byCode;
}

function numeroOuNulo(byCode, codigo) {
  const campo = byCode[codigo];
  if (!campo || campo.valor === null || campo.valor === undefined) return null;
  const numero = Number(campo.valor);
  return Number.isNaN(numero) ? campo.valor : numero;
}

function algumNaoNulo(...valores) {
  return valores.some(valor => valor !== null && valor !== undefined);
}

// Sufixo de fase no `nome` de um campo (ex.: "Tensão do motor - Fase A",
// "Motor voltage - Phase A") — único padrão que precisa de agrupamento manual
// (pedido de produto): vários campos com o mesmo nome-base, cada um de uma
// fase, viram uma única linha com os valores juntos ("134 | 138 | 135"), em
// vez de 1 linha por campo. A palavra "fase" varia com o idioma do
// DicionarioCampo (pt-BR/es-ES: Fase, en-US: Phase).
const PHASE_SUFFIX = /\s*-\s*(?:Fase|Phase)\s+\S+\s*$/i;

function hasPhaseSuffix(nome) {
  return PHASE_SUFFIX.test(nome || '');
}

function stripPhaseSuffix(nome) {
  return nome.replace(PHASE_SUFFIX, '').trim();
}

// Constrói as linhas de um card a partir dos `campos` crus do backend —
// usa `nome`/`valor`/`unidadeMedida` como vieram (já traduzidos), na ordem
// que o backend manda (`ordem`), agrupando por fase quando aplicável (ver
// PHASE_SUFFIX acima). Sem esse agrupamento, é 1 campo = 1 linha, sem
// nenhum mapa fixo de código → rótulo: um campo novo do backend aparece
// sozinho.
function buildDynamicRows(campos) {
  const ordenados = [...(campos || [])]
    .filter(campo => campo.valor !== null && campo.valor !== undefined && campo.valor !== '')
    .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));

  const basesAgrupadas = new Set();

  return ordenados.reduce((rows, campo) => {
    if (!hasPhaseSuffix(campo.nome)) {
      rows.push({
        label: campo.nome, value: campo.valor, unit: campo.unidadeMedida || '', codigo: campo.codigo,
      });
      return rows;
    }

    const base = stripPhaseSuffix(campo.nome);
    if (basesAgrupadas.has(base)) return rows; // já juntado no 1º campo dessa base
    basesAgrupadas.add(base);

    const doGrupo = ordenados
      .filter(c => hasPhaseSuffix(c.nome) && stripPhaseSuffix(c.nome) === base);
    rows.push({
      label: base,
      value: doGrupo.map(c => c.valor).join(' | '),
      unit: doGrupo[0].unidadeMedida || '',
      codigo: null, // vários campos originaram essa linha — nenhum `codigo` isolado a expor
    });
    return rows;
  }, []);
}

// Cada card de supervisão do backend traz `subtipo` (ex.: "BM", "Idm",
// "Gmp" — casing inconsistente entre módulos) identificando o módulo de
// origem — normaliza para maiúsculo para casar com CARD_KEY_BY_MODULE_CODE
// (mesma tabela usada por applyAlarmStatuses, ver abaixo).
function normalizeModuleCode(subtipo) {
  return String(subtipo || '').toUpperCase();
}

function findCardByModuleCode(cards, moduleCode) {
  return (cards || []).find(card => normalizeModuleCode(card.subtipo) === moduleCode) || null;
}

// "Dados Elétricos" é sintético (composto pelo backend a partir do cadastro
// do ativo, não de um módulo de supervisão) — é o único card sem
// `tipoModulo`, ao contrário de todos os cards reais (sempre "Supervisao").
function findElectricalDataCard(cards) {
  return (cards || []).find(card => !card.tipoModulo) || null;
}

// Todos os `codigo` que o mapeamento fixo de Buchas já conhece (ver
// CODES.bushings) — usado só para separar, do card real do backend, o que
// sobra fora desse mapeamento (ver buildBushings).
const KNOWN_BUSHINGS_CODES = new Set([
  ...Object.values(CODES.bushings.capacitance),
  ...Object.values(CODES.bushings.tangentDelta),
  ...Object.values(CODES.bushings.leakageCurrent),
]);

// Buchas mantém o layout fixo (1 linha por fase, 3 medidas juntas) só para
// os 9 campos já conhecidos (ver CODES.bushings/KNOWN_BUSHINGS_CODES) — um
// campo novo que o backend adicionar ao módulo BM não some: vira uma linha
// extra no padrão genérico dos outros cards (ver buildDynamicRows), anexada
// depois das linhas de fase (ver buildBushingsRows em cardsConfig.js).
function buildBushings(cards, byCode) {
  const phases = PHASES.map(phase => ({
    phase,
    capacitance: numeroOuNulo(byCode, CODES.bushings.capacitance[phase]),
    tangentDelta: numeroOuNulo(byCode, CODES.bushings.tangentDelta[phase]),
    leakageCurrent: numeroOuNulo(byCode, CODES.bushings.leakageCurrent[phase]),
  })).filter(row => algumNaoNulo(row.capacitance, row.tangentDelta, row.leakageCurrent));

  const card = findCardByModuleCode(cards, 'BM');
  const camposExtras = card
    ? (card.campos || []).filter(campo => !KNOWN_BUSHINGS_CODES.has(campo.codigo))
    : [];
  const extraRows = buildDynamicRows(camposExtras);

  // `title`: nome do card como o backend manda (já traduzido, ver
  // cardsConfig.js/CmaReadingCard.vue) — só existe quando o card veio na
  // resposta (`card`), senão o consumidor cai no `titleKey` estático.
  return phases.length || extraRows.length
    ? { phases, extraRows, title: card ? card.nome : null }
    : null;
}

function buildDynamicCard(cards, moduleCode) {
  const card = findCardByModuleCode(cards, moduleCode);
  if (!card) return null;
  const rows = buildDynamicRows(card.campos);
  return rows.length ? { rows, title: card.nome } : null;
}

// Só a primeira palavra do `nome` do campo (ex.: "Alarme Ruptura Membrana" →
// "Alarme") — regra pontual do módulo DM1 (bolsa, ver buildBagCard): os
// nomes que o backend manda pra esse módulo são longos demais pro layout de
// uma linha só, e não há um campo separado e mais curto pra usar como rótulo.
function primeiraPalavra(nome) {
  return String(nome || '').trim().split(/\s+/)[0] || '';
}

// Bolsa: igual a qualquer outro card dinâmico, com duas exceções pontuais —
// o rótulo de cada linha fica só com a 1ª palavra do nome do campo (ver
// primeiraPalavra acima), e o valor do campo de status é um código interno
// ("normal"/"critico", não texto para exibir direto) que precisa virar badge
// traduzido (ver CmaReadingCard.vue) — marca essa linha com `isStatus` para o
// componente saber qual delas traduzir, identificando-a pelo `codigo` (não
// pelo nome, que pode mudar).
function buildBagCard(cards) {
  const card = findCardByModuleCode(cards, 'DM1');
  if (!card) return null;
  const rows = buildDynamicRows(card.campos)
    .map(row => ({
      ...row,
      label: primeiraPalavra(row.label),
      ...(row.codigo === CODES.bagStatus ? { isStatus: true } : {}),
    }));
  return rows.length ? { rows, title: card.nome } : null;
}

// Dados Elétricos: card dinâmico igual aos demais, com uma exceção pontual —
// `assetInfo.specsLabel` (ver buildReadingsFromCards) precisa do valor bruto
// da potência nominal, não só da linha formatada para exibição.
function buildElectricalDataCard(cards) {
  const card = findElectricalDataCard(cards);
  if (!card) return null;
  const rows = buildDynamicRows(card.campos);
  if (!rows.length) return null;
  const potenciaCampo = (card.campos || []).find(campo => campo.codigo === CODES.electricalPower);
  const power = potenciaCampo ? Number(potenciaCampo.valor) : null;
  return {
    rows, power: Number.isNaN(power) ? null : power, title: card.nome,
  };
}

// Mapeia o código do módulo (extraído do prefixo de `nomeModulo` de um
// alarme — ver extractModuleCode) para a chave em cardStatuses/readings que
// ele afeta — pedido explícito de produto: BM (buchas), AVR (tensão na
// carga), IDM (comutador), GMP (gás e umidade), TM1 (temperaturas), MBR
// (bolsa). Módulos sem entrada aqui (ex.: dados elétricos, sintético) não
// têm regra de status por alarme.
//
// IMPORTANTE: a bolsa é o único módulo em que este código diverge do
// `subtipo` usado para achar o card em si (ver buildBagCard, que casa por
// 'DM1') — o endpoint de alarmes (alarme/detalhes) ainda manda o prefixo
// antigo "MBR" em `nomeModulo`, mesmo com `/supervisao/{id}/cards` já tendo
// migrado o `subtipo` do card para "DM1". Os dois códigos não têm relação
// entre si além de identificarem o mesmo módulo físico — confirmar com o
// backend antes de tentar unificá-los de novo.
const CARD_KEY_BY_MODULE_CODE = {
  BM: 'bushings',
  AVR: 'loadVoltage',
  IDM: 'commutator',
  GMP: 'gasAndHumidity',
  TM1: 'temperatures',
  MBR: 'bag',
};

// `DetalheDeAlarmeDto.NomeModulo` (endpoint alarme/detalhes) é o nome de
// exibição do módulo, sempre no formato "CÓDIGO - Descrição" ou "CÓDIGO –
// Descrição" (traço normal ou travessão) — confirmado no cadastro real de
// cada módulo usado pelo CMA (spec.md, decisão 9): "BM - Monitor de bucha",
// "GMP - Sensor Gás e Umidade...", "IDM - Monitor para Torque Comutador",
// "MBR – Relé de Ruptura de Membrana/Bolsa". Só o código antes do traço
// interessa aqui.
function extractModuleCode(moduleName) {
  const match = /^([A-Za-z0-9]+)\s*[-–]/.exec(moduleName || '');
  return match ? match[1].toUpperCase() : null;
}

// Só "crítico" (classificação máxima do backend) conta como crítico no
// painel — as outras (observação/ordinário/urgente) somam em "atenção":
// o painel do Figma só distingue 2 níveis, e colapsar tudo que não é
// crítico em "atenção" evita subestimar risco (decisão de produto).
// `classificacao` vem cru do backend (enum ClassificacaoAlarme serializado
// pelo próprio nome em português: "Critico"/"Urgente"/"Ordinario") — mesmo
// valor usado no filtro de alarme/detalhes, sem tradução intermediária.
function classificationToLevel(classification) {
  return String(classification || '').toLowerCase() === 'critico' ? 'critical' : 'attention';
}

// Sobrescreve, em `cardStatuses`, a severidade dos cards cujo módulo tem um
// alarme ativo (ver CARD_KEY_BY_MODULE_CODE/extractModuleCode) — só cards
// que já têm dado (chave pré-existente em `cardStatuses`, adicionada acima a
// partir de qual card veio na resposta) podem ganhar status por alarme; um
// alarme de módulo sem card em tela é ignorado. Um alarme crítico nunca é
// rebaixado por um alarme de atenção do mesmo módulo, seja qual for a ordem
// de iteração.
function applyAlarmStatuses(cardStatuses, alarms) {
  return (alarms || []).reduce((statuses, alarm) => {
    const statusKey = CARD_KEY_BY_MODULE_CODE[extractModuleCode(alarm.nomeModulo)];
    if (!statusKey || !(statusKey in statuses)) return statuses;
    const isCritical = classificationToLevel(alarm.classificacao) === 'critical';
    if (!isCritical && statuses[statusKey] === 'critical') return statuses;
    return { ...statuses, [statusKey]: isCritical ? 'critical' : 'attention' };
  }, cardStatuses);
}

// Status geral do ativo (badge do header, ver CmaDashboard.vue
// `healthStatusKey`) — reflete o alarme ativo mais grave entre os módulos
// exibidos no CMA (mesmo filtro de CARD_KEY_BY_MODULE_CODE usado por
// applyAlarmStatuses acima): crítico se houver algum alarme crítico nesses
// módulos, atenção se houver qualquer outro alarme ativo neles, "healthy"
// caso contrário. Alarme de módulo fora do CMA (sem card em tela) não
// interfere no badge do header.
function computeAssetCondition(alarms) {
  const relevantAlarms = (alarms || [])
    .filter(alarm => extractModuleCode(alarm.nomeModulo) in CARD_KEY_BY_MODULE_CODE);
  if (!relevantAlarms.length) return 'healthy';
  const hasCritical = relevantAlarms
    .some(alarm => classificationToLevel(alarm.classificacao) === 'critical');
  return hasCritical ? 'critical' : 'attention';
}

// Chaves de módulo em `readings` que viram card em tela (ver cardsConfig.js,
// CARD_BUILDERS) — usado só pelo fallback de leitura vazia logo abaixo.
const MODULE_KEYS = [
  'bushings', 'gasAndHumidity', 'temperatures', 'loadVoltage', 'bag', 'commutator', 'electricalData',
];

// Uma leitura de supervisão (60s, ver REFRESH_INTERVAL_MS em
// CmaDashboard.vue) pode vir com um módulo momentaneamente sem nenhum campo
// preenchido (falha pontual de comunicação com o sensor/device, não o
// módulo deixando de existir no ativo) — sem esse fallback, o card
// correspondente simplesmente sumia da tela até a próxima leitura trazer
// dado de novo, um "pisca" sem motivo aparente pro usuário. Mantém o último
// dado bom conhecido enquanto a leitura atual não trouxer nada NOVO pra
// aquele módulo; só fica `null` (card ausente) se a leitura ANTERIOR também
// não tinha dado — cobre o caso legítimo de módulo que o ativo não tem (ver
// buildCards em cardsConfig.js, "sem dado → omitido").
function withStaleFallback(modules, previousReadings) {
  if (!previousReadings) return modules;
  return MODULE_KEYS.reduce((acc, key) => {
    if (acc[key] !== null || !previousReadings[key]) return acc;
    return { ...acc, [key]: previousReadings[key] };
  }, modules);
}

// `previousReadings` é o `readings` da leitura anterior desse MESMO ativo
// (ver cmaDashboardService.getReadings/CmaDashboard.vue loadReadings) — null
// na 1ª leitura de um ativo (inclusive ao trocar de ativo selecionado), caso
// em que não há o que preservar.
// eslint-disable-next-line import/prefer-default-export
export function buildReadingsFromCards(cards, alarms, previousReadings = null) {
  const byCode = indexCamposPorCodigo(cards);

  const rawModules = {
    bushings: buildBushings(cards, byCode),
    gasAndHumidity: buildDynamicCard(cards, 'GMP'),
    temperatures: buildDynamicCard(cards, 'TM1'),
    loadVoltage: buildDynamicCard(cards, 'AVR'),
    bag: buildBagCard(cards),
    commutator: buildDynamicCard(cards, 'IDM'),
    electricalData: buildElectricalDataCard(cards),
  };
  const {
    bushings, gasAndHumidity, temperatures, loadVoltage, bag, commutator, electricalData,
  } = withStaleFallback(rawModules, previousReadings);

  // Só entra em cardStatuses quem realmente tem dado — os consumidores
  // (CmaDashboard/CmaReadingCard) tratam a ausência da chave como "sem
  // destaque", nunca como erro (ver getStatusLabelKey).
  const baseCardStatuses = {};
  if (bushings) baseCardStatuses.bushings = 'normal';
  if (gasAndHumidity) baseCardStatuses.gasAndHumidity = 'normal';
  if (temperatures) baseCardStatuses.temperatures = 'normal';
  if (loadVoltage) baseCardStatuses.loadVoltage = 'normal';
  if (bag) baseCardStatuses.bag = 'normal';
  if (commutator) baseCardStatuses.commutator = 'normal';
  if (electricalData) baseCardStatuses.electricalData = 'normal';
  const cardStatuses = applyAlarmStatuses(baseCardStatuses, alarms);

  return {
    bushings,
    gasAndHumidity,
    temperatures,
    loadVoltage,
    bag,
    commutator,
    electricalData,
    // Sem fonte no backend ainda (ver cabeçalho do arquivo) — painel
    // correspondente (CmaInfoPanel) fica oculto quando paperInfo é null.
    paperInfo: null,
    assetInfo: {
      specsLabel: electricalData && electricalData.power !== null
        ? `${electricalData.power} MVA`
        : '',
    },
    cardStatuses,
    // generalStatus/nextMaintenanceDate/operatingDays: sem fonte no backend
    // ainda — placeholders textuais simples, exibidos como estão (nunca
    // formatados como se fossem um dado real). assetCondition já tem fonte
    // real (ver computeAssetCondition) — não é mais placeholder.
    summary: {
      generalStatus: 'normal',
      assetCondition: computeAssetCondition(alarms),
      nextMaintenanceDate: null,
      operatingDays: '—',
    },
  };
}
